import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { Box } from '@mui/material';
import { showGlobalSuccess } from '@/components/feedback';

import {
  Row,
  Text,
  MobileButton,
  MobileInput,
  Background,
  MobileSelect,
} from '@/components/ui';
import { Layout, Header, useFooterContext } from '@/components/layout';
import { useProjectsStore } from '@/features/projects/store';

export const NewProject = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Stores
  const { createProject, getProjects, getMaterialListFormats } =
    useProjectsStore(state => state.actions);
  const existingProjects = useProjectsStore(state => state.projects);
  const { setFooterPlusButton } = useFooterContext();
  const { materialListFormats } = useProjectsStore();
  const [projectName, setProjectName] = useState('');
  const [nameError, setNameError] = useState('');
  const [currentProjectMLFormat, setCurrentProjectMLFormat] =
    useState<string>('');
  // Check for duplicate project name
  const checkProjectNameDuplicate = (name: string) => {
    const trimmedName = name.trim().toLowerCase();
    return existingProjects.some(
      project => project.projectName.toLowerCase() === trimmedName
    );
  };

  const onChangeProjectName = (value: string) => {
    setProjectName(value);

    // Clear previous errors
    setNameError('');

    // Check for duplicate name
    if (value.trim().length > 0 && checkProjectNameDuplicate(value)) {
      setNameError('project_name_duplicate_error');
    }
  };
  const onChangeMaterialsListFormat = (value: string) => {
    setCurrentProjectMLFormat(value);
  };
  const createProjectName = async () => {
    if (!projectName.trim()) return;

    // Final validation before creating
    if (checkProjectNameDuplicate(projectName)) {
      setNameError('project_name_duplicate_error');
      return;
    }

    const result = await createProject({
      projectName: projectName.trim(),
      description: '',
      discount: 0,
      materialFormatId: parseInt(currentProjectMLFormat),
    });

    if (result.success) {
      showGlobalSuccess(
        result.message || t('project_successfully_created_toast_text')
      );

      const newProjectId = result.data?.id;
      if (newProjectId) {
        navigate({ to: `/projects/${newProjectId}/sites` });
      } else {
        navigate({ to: '/projects' });
      }
    }
  };

  // Check if form is valid
  const isFormValid = () => {
    return (
      projectName.trim().length > 0 && // Project name is required
      currentProjectMLFormat && // Material list format is required
      !nameError // No name error
    );
  };

  // Load existing projects and hide footer plus button
  useEffect(() => {
    getMaterialListFormats(); // Load material list formats for the select dropdown
    getProjects(); // Load existing projects for duplicate checking
    setFooterPlusButton(false);

    return () => {
      setFooterPlusButton(false);
    };
  }, [getProjects, setFooterPlusButton]);

  const mLFormats = useMemo(() => {
    return materialListFormats.map(item => ({
      value: item.id.toString(),
      label: item.formatName,
    }));
  }, [materialListFormats]);

  return (
    <Layout>
      <Box sx={{ position: 'relative' }}>
        <Header
          title={t('title_new_project_page')}
          hideBackButton={false}
          linkBack='/projects'
        />

        <Box>
          <Row alignItems={'flex-end'} sx={{ mt: 2 }}>
            <MobileInput
              label={`${t('project_name_label_project_management')} *`}
              name='project_name'
              error={!!nameError}
              helperText={nameError}
              onChange={value => onChangeProjectName(value.target.value)}
              value={projectName}
              compact
              placeholder={''}
              fullWidth
            />
          </Row>
          <Row alignItems={'flex-end'} sx={{ mt: 2 }}>
            <MobileSelect
              title={t('materials_list_format_label_project_management')}
              items={mLFormats}
              error={false}
              noSelectLabel={''}
              onChange={value => onChangeMaterialsListFormat(value)}
              value={currentProjectMLFormat}
              fullWidth
            />
          </Row>
          <Row sx={{ mt: 2 }}>
            <MobileButton
              disabled={!isFormValid()}
              btnColor={isFormValid() ? '#1C5B9C' : '#CCCCCC'}
              sx={{
                width: '160px',
                height: '40px',
                mt: 0,
                opacity: isFormValid() ? 1 : 0.6,
                cursor: isFormValid() ? 'pointer' : 'not-allowed',
              }}
              onClick={createProjectName}
            >
              <Text sx={{ color: isFormValid() ? 'white' : '#666666' }}>
                {`${t('save_project_name_project_management')}`}
              </Text>
            </MobileButton>
          </Row>
        </Box>

        <Background />
      </Box>
    </Layout>
  );
};
